import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { ParsedParam } from "@/lib/mcp/schema";
import { useInspectorStore } from "@/stores/inspectorStore";
import { Play, RotateCcw } from "lucide-react";
import { ParameterInput } from "../shared/components";
import { useParameterManagement } from "../shared/hooks/useParameterManagement";

export interface PromptParameterFormProps {
  promptName: string | null;
  parsedParams: ParsedParam[];
  onExecute: () => void;
  isExecuting: boolean;
  canExecute: boolean;
  showParametersChanged?: boolean;
}

export function PromptParameterForm({
  promptName,
  parsedParams,
  onExecute,
  isExecuting,
  canExecute,
  showParametersChanged = false,
}: PromptParameterFormProps) {
  const {
    getPromptState,
    updatePromptArguments,
    updatePromptArgErrors,
    setPromptResponse,
    invalidateConfigureSection,
  } = useInspectorStore();

  const { paramValues, paramErrors, validateAndSetParameter, defaultValues } =
    useParameterManagement({
      type: "prompt",
      selectedName: promptName,
      parsedParams,
      getState: getPromptState,
      updateParameters: updatePromptArguments,
      updateErrors: updatePromptArgErrors,
      setResponse: setPromptResponse,
      invalidateConfigureSection,
    });

  const hasErrors = Object.values(paramErrors).some((error) => error);

  const resetToDefaults = () => {
    if (!promptName) {
      return;
    }

    updatePromptArguments(promptName, defaultValues);
    updatePromptArgErrors(promptName, {});
  };

  if (!promptName) {
    return null;
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (canExecute && !hasErrors) {
      onExecute();
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Parameters Changed Indicator */}
      {showParametersChanged && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-lg font-medium">Arguments</span>
          </div>
          <Badge variant="destructive" className="text-xs">
            Arguments changed - re-execute needed
          </Badge>
        </div>
      )}

      {/* Arguments Section */}
      <div>
        {parsedParams.length === 0 ? (
          <div className="text-sm text-muted-foreground p-4 border border-dashed rounded-lg text-center">
            This prompt takes no arguments.
          </div>
        ) : (
          <div className="space-y-4">
            {parsedParams.map((param) => (
              <ParameterInput
                key={param.name}
                parameter={param}
                value={paramValues[param.name] ?? ""}
                error={paramErrors[param.name]}
                onChange={(value) =>
                  validateAndSetParameter(param.name, value, param)
                }
                idPrefix="prompt-arg"
              />
            ))}
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={resetToDefaults}
          disabled={isExecuting}
        >
          <RotateCcw className="w-4 h-4 mr-2" />
          Reset
        </Button>
        <Button
          type="submit"
          size="sm"
          disabled={!canExecute || hasErrors || isExecuting}
          className="flex-1"
        >
          <Play className="w-4 h-4 mr-2" />
          {isExecuting ? "Executing..." : "Execute Prompt"}
        </Button>
      </div>
    </form>
  );
}
